import type {
    ConversationClassifier,
    FocusedConversationInsightSource,
} from "services/ConversationService";

const CALL_DRIVERS_CLASSIFIER = "ML-CallDrivers";
const TOPICS_CLASSIFIER = "ML-Topic-LLM";

export type FocusedConversationInsight = {
    name: string;
    description: string | null;
};

export type FocusedConversationInsights = {
    title: "Key Drivers" | "Key Topics";
    emptyMessage: string;
    items: FocusedConversationInsight[];
};

const parseClassifierResult = (
    classifier: ConversationClassifier | undefined,
): Record<string, unknown> | null => {
    if (!classifier?.result) return null;

    try {
        const result: unknown = JSON.parse(classifier.result);
        return result !== null &&
            typeof result === "object" &&
            !Array.isArray(result)
            ? (result as Record<string, unknown>)
            : null;
    } catch {
        return null;
    }
};

const stringValue = (
    result: Record<string, unknown> | null,
    key: string,
): string | null => {
    const value = result?.[key];
    return typeof value === "string" && value.trim().length > 0
        ? value.trim()
        : null;
};

const isTopicsClassifier = (classifier: ConversationClassifier): boolean => {
    if (classifier.name === TOPICS_CLASSIFIER) return true;
    if (!classifier.resultJson) return false;

    try {
        const metadata: unknown = JSON.parse(classifier.resultJson);
        return (
            metadata !== null &&
            typeof metadata === "object" &&
            !Array.isArray(metadata) &&
            (metadata as Record<string, unknown>).ModelName ===
                TOPICS_CLASSIFIER
        );
    } catch {
        return false;
    }
};

const buildItems = (
    result: Record<string, unknown> | null,
    nameKey: (index: number) => string,
    reasoningKey: (index: number) => string,
    includeName: (name: string) => boolean,
): FocusedConversationInsight[] =>
    [1, 2, 3].flatMap((index) => {
        const name = stringValue(result, nameKey(index));
        if (!name || !includeName(name)) return [];

        return [
            {
                name,
                description: stringValue(result, reasoningKey(index)),
            },
        ];
    });

export const getLegacyTopicInsights = (
    classifiers: ConversationClassifier[],
): FocusedConversationInsight[] => {
    const result = parseClassifierResult(classifiers.find(isTopicsClassifier));

    return buildItems(
        result,
        (index) => `Topic ${index}`,
        (index) => `Topic ${index} Reasoning`,
        (name) => name !== "NA",
    );
};

export const getFocusedConversationInsights = (
    source: FocusedConversationInsightSource,
    classifiers: ConversationClassifier[],
): FocusedConversationInsights | null => {
    if (source === "Drivers") {
        const result = parseClassifierResult(
            classifiers.find(
                (classifier) => classifier.name === CALL_DRIVERS_CLASSIFIER,
            ),
        );

        return {
            title: "Key Drivers",
            emptyMessage: "No drivers have been generated on this conversation",
            items: buildItems(
                result,
                (index) => `CallDriver ${index}`,
                (index) => `CallDriver ${index} Reasoning`,
                (name) => name !== "NA",
            ),
        };
    }

    if (source === "Topics") {
        return {
            title: "Key Topics",
            emptyMessage: "No topics have been generated on this conversation",
            items: getLegacyTopicInsights(classifiers),
        };
    }

    return null;
};
